import { useEffect, useState } from 'react';

import { OverflowTabs } from 'components';
import { lodash } from 'helpers';
import { useTabsNavigate } from 'hooks';
import { useFormContext } from 'libs/form/useFormContext';
import { Stack } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';

export interface FormTabsProps<ID extends string = string> {
  tabs: {
    id: ID;
    label: string;
    content: React.ReactNode;
    formSectionKeys: string[];
  }[];
  onTabChange?: (event: React.SyntheticEvent, tabId: ID) => void;
  onInitialTab?: (tabId: ID) => void;
}

function isDeepEmpty(value: any): boolean {
  if (typeof value === 'object' && value !== null) {
    if (lodash.isPlainObject(value)) {
      return Object.values(value).every(isDeepEmpty);
    }
    if (Array.isArray(value)) {
      return value.every(isDeepEmpty);
    }
  }

  return lodash.isEmpty(value);
}

const isErrorInTab = (
  tab: FormTabsProps['tabs'][0],
  errors: Record<string, any>
) => {
  const result = tab.formSectionKeys.some((key) => !isDeepEmpty(errors[key]));

  return result;
};

const TabLabel = ({ label, isError }: { label: string; isError: boolean }) => (
  <Stack alignItems="center" direction="row" spacing={4}>
    {label} {isError && <Icon.Info color="inherit" />}
  </Stack>
);

export const FormTabs = <ID extends string>({
  tabs,
  onTabChange,
  onInitialTab,
}: FormTabsProps<ID>) => {
  const { errors, isValidating } = useFormContext();

  const [isNeedChangeTab, setIsNeedChangeTab] = useState(false);

  const { currentTabNumber, goToTab } = useTabsNavigate();

  const currentTab = tabs[currentTabNumber - 1] ?? tabs[0];

  const { content } = currentTab;

  const handleChange = (event: React.SyntheticEvent, newTabId: ID) => {
    onTabChange?.(event, newTabId);

    if (event.defaultPrevented) {
      return;
    }

    goToTab(tabs.findIndex((tab) => tab.id === newTabId) + 1);
  };

  useEffect(() => {
    if (isValidating) {
      setIsNeedChangeTab(true);
    }
  }, [isValidating]);

  useEffect(() => {
    if (errors && isNeedChangeTab) {
      const errorTabIndex = tabs.findIndex((tab) => isErrorInTab(tab, errors));

      if (errorTabIndex !== -1) {
        goToTab(errorTabIndex + 1);
        setIsNeedChangeTab(false);
      }
    }
  }, [errors, goToTab, isNeedChangeTab, tabs]);

  useEffect(() => {
    if (onInitialTab) {
      onInitialTab(currentTab.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack spacing={24}>
      <OverflowTabs
        tabs={tabs.map((tab) => {
          const isError = isErrorInTab(tab, errors);
          return {
            ...tab,
            isError,
            value: tab.id,
            label: <TabLabel isError={isError} label={tab.label} />,
          };
        })}
        value={currentTab.id}
        onChange={handleChange}
      />

      {content}
    </Stack>
  );
};
